import type { Plugin } from 'vue';
import PrimeVue from 'primevue/config';
import PVAutoComplete from 'primevue/autocomplete';
import PVBadge from 'primevue/badge';
import PVButton from 'primevue/button';
import PVButtonGroup from 'primevue/buttongroup';
import PVCheckbox from 'primevue/checkbox';
import PVDrawer from 'primevue/drawer';
import PVInputNumber from 'primevue/inputnumber';
import PVInputText from 'primevue/inputtext';
import PVMenu from 'primevue/menu';
import PVMessage from 'primevue/message';
import PVMultiSelect from 'primevue/multiselect';
import PVPopover from 'primevue/popover';
import PVProgressSpinner from 'primevue/progressspinner';
import PVSelect from 'primevue/select';
import PVSplitButton from 'primevue/splitbutton';
import PVTab from 'primevue/tab';
import PVTabList from 'primevue/tablist';
import PVTabs from 'primevue/tabs';
import PVToggleSwitch from 'primevue/toggleswitch';
import PVToolbar from 'primevue/toolbar';
import PVDivider from 'primevue/divider';
import PVTooltipDirective from 'primevue/tooltip';

import { useAppTheme } from '@/composables/useAppTheme';
import { getActiveAppTheme } from '@/themes';
import type { Preset } from '@primeuix/themes/types';

export const PRIME_VUE_COMPONENTS = {
  PVAutoComplete,
  PVBadge,
  PVButton,
  PVButtonGroup,
  PVCheckbox,
  PVDrawer,
  PVInputNumber,
  PVInputText,
  PVMenu,
  PVMessage,
  PVMultiSelect,
  PVPopover,
  PVProgressSpinner,
  PVSelect,
  PVSplitButton,
  PVTab,
  PVTabList,
  PVTabs,
  PVToggleSwitch,
  PVToolbar,
  PVDivider,
} as const;

type MissingPrimeVueComponentsInTypings = Exclude<
  keyof typeof PRIME_VUE_COMPONENTS,
  keyof import('vue').GlobalComponents
>;

/**
 * A type error here means a component was added to {@link PRIME_VUE_COMPONENTS} without a
 * matching entry in {@link file://../types/globalComponents.d.ts}.
 */
// oxlint-disable-next-line no-unused-vars
const _assertPrimeVueComponentsAreTyped: MissingPrimeVueComponentsInTypings extends never
  ? true
  : MissingPrimeVueComponentsInTypings = true;

export async function getPrimeVuePlugin(): Promise<Plugin> {
  const { setAppTheme, appTheme } = useAppTheme();
  setAppTheme(await getActiveAppTheme());

  return {
    install(vue) {
      const config: {
        licenseKey?: string;
        theme: {
          preset: Preset;
          options: {
            darkModeSelector: boolean;
          };
        };
      } = {
        theme: {
          preset: appTheme.value.primevuePreset,
          options: {
            // Client themes are light-only today; avoid system-driven dark CSS vars.
            darkModeSelector: false,
          },
        },
      };

      if (import.meta.env.VITE_PRIME_UI_LICENSE_KEY) {
        config.licenseKey = import.meta.env.VITE_PRIME_UI_LICENSE_KEY;
      }

      vue.use(PrimeVue, config);

      for (const [name, component] of Object.entries(PRIME_VUE_COMPONENTS)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (component as any).compatConfig = { MODE: 3 };
        vue.component(name, component);
      }

      // Directive (not a component) — consumed as v-tooltip via StandardTooltip.
      vue.directive('tooltip', PVTooltipDirective);
    },
  };
}
